import { Link, useParams } from 'react-router-dom';
import { CheckCircle, XCircle, MinusCircle, ArrowRight, Sparkles, Scale, TrendingUp, Shield } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo, { SITE_URL } from '../components/Seo';
import { COMPETITORS } from '../data/competitors';

function Mark({ value }) {
  if (value === true)  return <CheckCircle className="w-5 h-5 text-emerald-400" />;
  if (value === false) return <XCircle className="w-5 h-5 text-red-400/70" />;
  if (value === 'partial') return <MinusCircle className="w-5 h-5 text-amber-400" />;
  return <span className="text-sm text-gray-300">{String(value)}</span>;
}

function NotFound({ slug }) {
  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo title="Comparison not found" description="Page not found" path={`/vs/${slug}`} noIndex />
      <PublicNav />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="text-3xl font-bold mb-3">Comparison not found</h1>
        <p className="text-gray-400 mb-6">We do not have a head-to-head for that competitor yet.</p>
        <Link to="/comparison" className="text-amber-400 hover:text-amber-300 font-semibold">
          Browse all comparisons →
        </Link>
      </div>
    </div>
  );
}

export default function VsCompetitorPage() {
  const { competitor } = useParams();
  const data = COMPETITORS[competitor];

  if (!data) return <NotFound slug={competitor} />;

  const description =
    `${data.headline}. Full head-to-head comparison: features, pricing, ` +
    `migration steps, and why traders switch. ${data.subheading}`.slice(0, 200);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/vs/${data.slug}` },
      headline: data.headline,
      description,
      author: { '@type': 'Organization', name: 'haythix' },
      publisher: {
        '@type': 'Organization',
        name: 'haythix',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.png` },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `Is haythix a good ${data.name} alternative?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes. haythix covers everything ${data.name} does (${data.headToHeadFeatures.filter(f => f.them === true).length} shared features) and adds ${data.headToHeadFeatures.filter(f => f.haythix === true && f.them === false).length} that ${data.name} does not offer — including ${data.headToHeadFeatures.filter(f => f.haythix === true && f.them === false).slice(0, 3).map(f => f.feature).join(', ')}.`,
          },
        },
        {
          '@type': 'Question',
          name: `Why do users switch from ${data.name} to haythix?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: data.whySwitch.join(' ').slice(0, 500),
          },
        },
        {
          '@type': 'Question',
          name: `How do I migrate from ${data.name} to haythix?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: data.migration.join(' ').slice(0, 500),
          },
        },
        {
          '@type': 'Question',
          name: `How does haythix pricing compare to ${data.name}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `haythix entry tier is $10/mo vs ${data.pricingComparison[0]?.them}. Top tier is $149.99/mo (Elite) covering DEX + CEX + algo orders + tokenomics tools — categories that ${data.name} either does not offer or charges extra for.`,
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',        item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Comparison',  item: `${SITE_URL}/comparison` },
        { '@type': 'ListItem', position: 3, name: `vs ${data.name}`, item: `${SITE_URL}/vs/${data.slug}` },
      ],
    },
  ];

  const gainsVsLoses = {
    gains: data.headToHeadFeatures.filter(f => f.haythix === true && f.them === false),
    shared: data.headToHeadFeatures.filter(f => f.haythix === true && f.them === true),
    partial: data.headToHeadFeatures.filter(f => f.them === 'partial'),
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={`${data.name} alternative for 2026`}
        description={description}
        path={`/vs/${data.slug}`}
        jsonLd={jsonLd}
        type="article"
      />
      <PublicNav />

      {/* ─── Hero ────────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-semibold mb-5">
          <Scale className="w-3.5 h-3.5" /> Side-by-side comparison
        </div>
        <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
          {data.headline}
        </h1>
        <p className="text-gray-400 text-lg md:text-xl max-w-3xl">
          {data.subheading}
        </p>

        <div className="flex flex-wrap gap-3 mt-7">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-amber-500/20"
          >
            Try haythix free for 14 days <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-dark-600 hover:border-dark-500 text-gray-300 hover:text-white text-sm font-semibold transition-colors"
          >
            Compare pricing
          </Link>
        </div>
      </section>

      {/* ─── Gains / Shared summary ──────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
            <div className="flex items-center gap-2 mb-2 text-emerald-400">
              <Sparkles className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-widest">You gain</p>
            </div>
            <p className="text-3xl font-bold text-white">{gainsVsLoses.gains.length}</p>
            <p className="text-sm text-gray-400 mt-1">features haythix offers that {data.name} does not</p>
          </div>
          <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5">
            <div className="flex items-center gap-2 mb-2 text-blue-400">
              <Shield className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-widest">You keep</p>
            </div>
            <p className="text-3xl font-bold text-white">{gainsVsLoses.shared.length}</p>
            <p className="text-sm text-gray-400 mt-1">features shared with {data.name}</p>
          </div>
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="flex items-center gap-2 mb-2 text-amber-400">
              <TrendingUp className="w-5 h-5" />
              <p className="text-xs font-bold uppercase tracking-widest">You upgrade</p>
            </div>
            <p className="text-3xl font-bold text-white">{gainsVsLoses.partial.length}</p>
            <p className="text-sm text-gray-400 mt-1">{data.name} half-features that haythix does in full</p>
          </div>
        </div>
      </section>

      {/* ─── Feature matrix ──────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-2">Feature-by-feature comparison</h2>
        <p className="text-gray-400 text-sm mb-6">
          {data.headToHeadFeatures.length} features. Green = supported, red = missing, amber = partial.
        </p>
        <div className="rounded-2xl border border-dark-700/50 overflow-hidden bg-dark-800/40">
          <table className="w-full text-sm">
            <thead className="bg-dark-800">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-300">Feature</th>
                <th className="text-center p-4 font-semibold text-amber-400">haythix</th>
                <th className="text-center p-4 font-semibold text-gray-400">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.headToHeadFeatures.map((f, i) => (
                <tr key={i} className="border-t border-dark-700/40">
                  <td className="p-3 text-gray-200">{f.feature}</td>
                  <td className="p-3 text-center"><div className="inline-flex"><Mark value={f.haythix} /></div></td>
                  <td className="p-3 text-center"><div className="inline-flex"><Mark value={f.them} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── Pricing comparison ──────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-2">Pricing — haythix vs {data.name}</h2>
        <p className="text-gray-400 text-sm mb-6">Tier-by-tier comparison.</p>
        <div className="rounded-2xl border border-dark-700/50 overflow-hidden bg-dark-800/40">
          <table className="w-full text-sm">
            <thead className="bg-dark-800">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-300">Tier</th>
                <th className="text-left p-4 font-semibold text-amber-400">haythix</th>
                <th className="text-left p-4 font-semibold text-gray-400">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.pricingComparison.map((row, i) => (
                <tr key={i} className="border-t border-dark-700/40">
                  <td className="p-3 text-gray-200">{row.feature}</td>
                  <td className="p-3 text-emerald-300">{row.haythix}</td>
                  <td className="p-3 text-gray-400">{row.them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── Why switch ──────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-6">Why traders switch from {data.name} to haythix</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {data.whySwitch.map((reason, i) => (
            <div key={i} className="rounded-xl border border-dark-700/50 bg-dark-800/40 p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-bold text-amber-400">{i + 1}</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">{reason}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Migration steps ─────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-2">How to migrate from {data.name}</h2>
        <p className="text-gray-400 text-sm mb-6">Most users complete the switch in under 30 minutes.</p>
        <ol className="space-y-3">
          {data.migration.map((step, i) => (
            <li key={i} className="flex gap-4 rounded-xl border border-dark-700/50 bg-dark-800/40 p-5">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <span className="text-sm font-bold text-amber-400">{i + 1}</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{step}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ─── What competitor does well (honest framing) ─────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-2">What {data.name} does well</h2>
        <p className="text-gray-400 text-sm mb-6">A fair view — {data.name} has real strengths worth naming.</p>
        <ul className="grid md:grid-cols-2 gap-3">
          {data.competitorStrengths.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" /> {s}
            </li>
          ))}
        </ul>
      </section>

      {/* ─── Final CTA ──────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-dark-800 to-dark-900 p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Try haythix free for 14 days — every feature, no card required
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            See the {gainsVsLoses.gains.length} features you do not get on {data.name}, with full access during the trial.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-amber-500/20"
            >
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-dark-600 hover:border-dark-500 text-gray-300 hover:text-white text-sm font-semibold transition-colors"
            >
              See all pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Related comparisons */}
      <section className="max-w-5xl mx-auto px-6 py-10 border-t border-dark-700/40">
        <p className="text-xs uppercase tracking-widest text-amber-400/80 mb-4">More comparisons</p>
        <div className="flex flex-wrap gap-2">
          {Object.values(COMPETITORS)
            .filter((c) => c.slug !== data.slug)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/vs/${c.slug}`}
                className="text-xs px-3 py-1.5 rounded-full border border-dark-700 hover:border-amber-500/40 text-gray-400 hover:text-amber-300 transition-colors"
              >
                haythix vs {c.name}
              </Link>
            ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
